<i18n>
{
  "fr": {
    "outward": "Aller",
    "return": "Retour",
    "outward-and-return": "Aller et Retour",
    "luggage": "Bagages",
    "optional": "En option :",
    "included": "Inclus* :",
    "per-person": "* Par personne",
    "no-optional-luggage": "Pas de bagages en option",
    "total-price": "Prix total des bagages en option"
  },
  "en": {
    "outward": "Outward",
    "return": "Return",
    "outward-and-return": "Outward and Return",
    "luggage": "Luggage",
    "optional": "Optional :",
    "included": "Included :",
    "per-person": "* Per person",
    "no-optional-luggage": "No luggage options",
    "total-price": "Optional luggage total price"
  }
}
</i18n>

<template>
  <v-card
    class="my-2"
    flat
  >
    <v-card-title>
      <h1 class="text--primary title">
        {{ $t('luggage') }}
      </h1>
    </v-card-title>
    <h4 class="grey--text text--darken-3 ml-8 mb-1">
      {{ $t('included') }}
    </h4>

    <LuggageIncludedInfo
      class="ml-3"
      :outward-included-luggage="itinerary.flight_outward.details[0].included_luggage"
      :return-included-luggage="itinerary.flight_return && itinerary.flight_return.details[0].included_luggage"
    />

    <v-divider
      inset
      class="mx-auto mt-3 mb-5"
    />
    <h4 class="grey--text text--darken-3 ml-8">
      {{ $t('optional') }}
    </h4>
    <v-card-text>
      <v-row class="ml-3">
        <v-col
          v-if="!luggageOptionsArray.length"
          class="d-flex align-center"
        >
          <v-icon
            class="mr-5"
            color="grey"
          >
            mdi-bag-carry-on-off
          </v-icon>
          <span>{{ $t('no-optional-luggage') }}</span>
        </v-col>
        <v-col
          v-for="option in luggageOptionsArray"
          v-else
          :key="option.id"
          :cols="option.commonSelectedOption || option.id === 'NO_OPTIONS' ? 3 : 5"
        >
          <v-card
            outlined
          >
            <v-card-text>
              <template v-if="option.title">
                <v-row class="text-xl-caption font-weight-bold ml-1 mb-2">
                  {{ option.title }}
                </v-row>
                <v-divider class="grey lighten-3" />
              </template>
              <div
                v-if="option.id === 'NO_OPTIONS'"
                class="d-flex justify-center align-center text--disabled mt-5"
              >
                <v-icon class="mr-2 text--disabled">
                  mdi-bag-carry-on-off
                </v-icon><span>{{ $t('no-optional-luggage') }}</span>
              </div>
              <template v-else>
                <v-row class="mt-1">
                  <v-col
                    v-if="option.commonSelectedOption"
                    class="text-center"
                  >
                    <i>{{ travelType === 'one-way' ? $t('outward') : $t('outward-and-return') }}</i>
                  </v-col>
                  <template v-else>
                    <v-col class="text-center">
                      <i>{{ $t('outward') }}</i>
                    </v-col>
                    <v-col class="text-center">
                      <i>{{ $t('return') }}</i>
                    </v-col>
                  </template>
                </v-row>
                <v-row v-if="option.commonSelectedOption">
                  <FlightLuggageOptionDetails
                    :selected-option="option.commonSelectedOption"
                  />
                </v-row>
                <v-row v-else>
                  <v-col class="pa-0">
                    <FlightLuggageOptionDetails
                      :selected-option="option.outwardSelectedOption"
                    />
                  </v-col>
                  <v-divider
                    vertical
                    class="grey lighten-3"
                  />
                  <v-col class="pa-0">
                    <FlightLuggageOptionDetails
                      :selected-option="option.returnSelectedOption"
                    />
                  </v-col>
                </v-row>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <div class="d-flex justify-space-between align-center mt-4">
        <span class="caption ml-3">{{ $t('per-person') }}</span>
        <h2
          v-if="luggageOptionsArray.length"
        >
          {{ $t('total-price') }} : <span id="total-price">{{ formatPriceWithCode(itinerary.price.luggage_amount, itinerary.price.currency) }}</span>
        </h2>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import FlightLuggageOptionDetails
  from '@/components/Flight/FlightOrderSummary/FlightLuggageOption/FlightLuggageOptionDetails/FlightLuggageOptionDetails'
import globalMixin from '@/mixins/global'
import LuggageIncludedInfo
  from '@/components/Flight/FlightPassengersInformation/FlightTravellerDetails/LuggageOptionsPerPassenger/LuggageIncludedInfo/LuggageIncludedInfo'

export default {
  name: 'FlightLuggageOption',
  components: { LuggageIncludedInfo, FlightLuggageOptionDetails },
  mixins: [globalMixin],
  computed: {
    ...mapState({
      itinerary: state => state.fb.searchResults.updatedSelectedItinerary,
      travellers: state => state.fb.passengersInformation.selectedTravellersDetails,
      selectedLuggageOption: state => state.fb.passengersInformation.selected_luggage_option,
      travelType: state => state.fb.searchResults.travelType
    }),
    luggageOptionsArray () {
      // Create options array per passenger
      const luggageOptionsPerPassenger = this.travellers.map((traveller) => {
        const outwardSelectedOption = traveller.selected_luggage_option?.OutwardLuggageOptions?.selected_value
        const returnSelectedOption = traveller.selected_luggage_option?.ReturnLuggageOptions?.selected_value
        const commonSelectedOption = traveller.selected_luggage_option?.LuggageOptions?.selected_value

        const name = `${traveller.first_name} ${traveller.last_name}`
        const id = traveller.traveller_id

        if (outwardSelectedOption || returnSelectedOption || commonSelectedOption) {
          return {
            id,
            title: name,
            outwardSelectedOption: outwardSelectedOption || 'NO_OPTION_SELECTED',
            returnSelectedOption: returnSelectedOption || 'NO_OPTION_SELECTED',
            commonSelectedOption
          }
        }
        return {
          id: 'NO_OPTIONS',
          title: name
        }
      })

      // if all passenger contains no selected options, empty array
      if (luggageOptionsPerPassenger.length === luggageOptionsPerPassenger.filter(e => e.id === 'NO_OPTIONS').length) {
        luggageOptionsPerPassenger.length = 0
      }

      // Create option per booking (common)
      const outwardSelectedOption = this.selectedLuggageOption?.OutwardLuggageOptions?.selected_value
      const returnSelectedOption = this.selectedLuggageOption?.ReturnLuggageOptions?.selected_value
      const commonSelectedOption = this.selectedLuggageOption?.LuggageOptions?.selected_value

      if (outwardSelectedOption || returnSelectedOption || commonSelectedOption) {
        const luggageOptionsPerBooking = {
          id: this.$i18n.t('for-all-passengers'),
          outwardSelectedOption: outwardSelectedOption || 'NO_OPTION_SELECTED',
          returnSelectedOption: returnSelectedOption || 'NO_OPTION_SELECTED',
          commonSelectedOption
        }
        // Return concatenated options if perBookingOption exist
        return [luggageOptionsPerBooking, ...luggageOptionsPerPassenger]
      }

      // Else return only perPassenger options
      return luggageOptionsPerPassenger
    }
  }
}
</script>

<style scoped>
#total-price {
  color: #0089ba;
}

</style>
